/**
 * Converts an object to a jQuery object
 * if it's not already a jQuery object
 * @param {*} htmlElementOrJqueryObject - The object to convert
 */
export function convertTojQuery(htmlElementOrJqueryObject) {
    if (isjQueryObject(htmlElementOrJqueryObject)) {
        // element is already a jquery object
        return htmlElementOrJqueryObject;
    }

    // create new jquery object
    return $(htmlElementOrJqueryObject);
}

/**
 * Checks if the given object is a jQuery object
 * @param {*} object - The object to check
 */
export function isjQueryObject(object) {
    return object instanceof jQuery;
}

/**
 * No operation function
 */
export function noop() { }

/**
 * Returns the offset height of an element
 * @param {*} htmlElementOrJqueryObject - The element to measure
 */
export function getElementHeight(htmlElementOrJqueryObject) {
    let element;

    if (isjQueryObject(htmlElementOrJqueryObject)) {
        // element is already a jquery object
        // eslint-disable-next-line prefer-destructuring
        element = htmlElementOrJqueryObject[0];
    } else {
        element = htmlElementOrJqueryObject;
    }

    return Math.round(element.offsetHeight);
}

/**
 * Returns the offset height of an element
 * @param {*} htmlElementOrJqueryObject - The element to measure
 */
export function getElementScrollHeight(htmlElementOrJqueryObject) {
    let element;

    if (isjQueryObject(htmlElementOrJqueryObject)) {
        // element is already a jquery object
        // eslint-disable-next-line prefer-destructuring
        element = htmlElementOrJqueryObject[0];
    } else {
        element = htmlElementOrJqueryObject;
    }

    return Math.round(element.scrollHeight);
}

/**
 * Checks if the given event corresponds
 * to an click event or a keydown event
 * for a enter key press.
 * @param {Event} e - The event object to check
 */
export function isClickOrEnter(e) {
    if (e) {
        let event;

        if (Object.prototype.hasOwnProperty.call(e, 'originalEvent')) {
            // this is probably a jQuery event so get the originalEvent
            event = e.originalEvent;
        } else {
            event = e;
        }

        if (event instanceof UIEvent) {
            return e.type === 'click' || (e.type === 'keydown' && e.keyCode === 13);
        }
    }

    return false;
}

export function cloneArray(array) {
    return [...array];
}

export function clickAnchor(href, target) {
    const a = document.createElement('a');
    a.href = href;

    if (target) {
        a.target = target;
    }

    a.click();
}

export const generateId = (() => {
    const generatedIds = new Set();
    const letterCharset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIKLMNOPQRSTUVWXYZ';
    const numberCharset = '0123456789';
    const completeCharset = letterCharset + numberCharset;

    function pickRandomCharacter(charset) {
        return charset[Math.floor(Math.random() * charset.length)];
    }

    function generate(length) {
        // first character should always be a letter
        // so pick a random character from the
        // letter charset
        let id = pickRandomCharacter(letterCharset);

        for (let i = 0, iLen = length - 1; i < iLen; i++) {
            // then continue picking characters from the complete charset
            id += pickRandomCharacter(completeCharset);
        }

        return id;
    }

    return (length) => {
        const len = length || 8;

        let id;

        do {
            id = generate(len);
        }
        while (generatedIds.has(id));

        generatedIds.add(id);
        return id;
    };
})();

export const replacePlaceholders = (input, replacements, placeholderStart = '\\{', placeholderEnd = '\\}') => {
    if (!input || !replacements) {
        return input;
    }

    const keys = Object.keys(replacements);
    let replacedResult = input;

    keys.forEach((key) => {
        const pattern = `${placeholderStart}\\s*${key}\\s*${placeholderEnd}`;
        const replacement = replacements[key];
        const replacementValue = (replacement === null || replacement === undefined) ? '' : replacement;
        replacedResult = replacedResult.replace(new RegExp(pattern, 'g'), replacementValue);
    });

    return replacedResult;
};

/**
 * Copies the given text to the clipboard
 * @param {string} text The text to copy
 * @returns {Promise<boolean>} Promise which fulfills with
 * true or false depending on copy success
 */
export const copyTextToClipboard = (() => {
    const fallbackCopyTextToClipboard = (text) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.cssText = 'position: fixed; left: 100%; top: 100%; width: 1px; height: 1px; opacity: 0.01;';

        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();

        try {
            return document.execCommand('copy');
        } catch (error) {
            return false;
        } finally {
            // document.body.removeChild(textarea);
        }
    };

    return async (text) => {
        if (!navigator.clipboard) {
            return fallbackCopyTextToClipboard(text);
        }

        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            return false;
        }
    };
})();

/**
 * Executes the given callback function
 * on DOMContentLoaded or immediately
 * if the event was already fired.
 * @param callback Callback function to execute
 */
export const domReady = (() => {
    const callbacks = [];
    const bindDOMContentLoaded = () => {
        document.addEventListener('DOMContentLoaded', () => {
            callbacks.forEach((callback) => {
                try {
                    callback();
                } catch (error) {
                    // nothing
                }
            });
        });
    };

    return (callback) => {
        if (typeof callback !== 'function') {
            return;
        }

        switch (document.readyState) {
            case 'interactive':
            case 'complete':
                callback();
                break;
            default: {
                if (callbacks.length === 0) {
                    bindDOMContentLoaded();
                }

                callbacks.push(callback);
            }
        }
    };
})();